import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Button,
  Typography,
  TextField,
  Autocomplete,
} from "@mui/material";
import axios from "axios";
import ErrorModal from "../../utils/ErrorModal";

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const INK = "#0F1B2A";
const INK_2 = "#3D4C60";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#D5DEE7";
const PRIMARY = "#0B5C8A";

const acSx = {
  "& .MuiInputBase-root": {
    minHeight: 42,
    fontFamily: FONT,
    fontSize: 14,
    borderRadius: "10px",
    padding: "1px 7px",
    background: "#FFFFFF",
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: FIELD_EDGE },
  "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#B4C6DA" },
  "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: PRIMARY, borderWidth: 1 },
  "& .MuiInputLabel-root": { fontFamily: FONT, fontSize: 14, color: MUTED },
  "& .MuiInputLabel-root.Mui-focused": { color: PRIMARY },
};
const paperSx = {
  mt: 0.5,
  borderRadius: "12px",
  border: `1px solid ${LINE}`,
  boxShadow: "0 16px 36px -12px rgba(15,27,42,0.24)",
  "& .MuiAutocomplete-option": { fontFamily: FONT, fontSize: 13.5, borderRadius: "8px", mx: 0.75 },
  "& .MuiAutocomplete-groupLabel": {
    fontFamily: FONT,
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: "0.09em",
    textTransform: "uppercase",
    color: MUTED,
    lineHeight: "30px",
  },
};
const labelStyle = {
  display: "block",
  margin: "0 0 6px",
  fontFamily: FONT,
  fontSize: 12,
  fontWeight: 600,
  color: INK_2,
};

const LeadTransferModal = ({
  open,
  onClose,
  lead,
  currentProjectId,
  onSuccess,
  role,
}) => {
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const orgId = JSON.parse(localStorage.getItem("org"));

  const [projects, setProjects] = useState([]);
  const [statuses, setStatuses] = useState([]);

  const [project, setProject] = useState(null);
  const [status, setStatus] = useState(null);

  const [loading, setLoading] = useState(false);

  const [msgOpen, setMsgOpen] = useState(false);
  const [msgText, setMsgText] = useState("");
  const showError = (msg) => {
    setMsgText(msg);
    setMsgOpen(true);
  };

  useEffect(() => {
    if (!open) return;

    const fetchProjects = async () => {
      try {
        if (role === "ADMIN") {
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/project/all?orgId=${orgId}`,
            { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
          );
          setProjects(res.data.filter((p) => p.projectId !== currentProjectId));
        } else {
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/project/team/all?orgId=${orgId}`,
            { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
          );
          setProjects(res.data.filter((p) => p.projectId !== currentProjectId));
        }
      } catch (error) {
        console.log(error);
      }
    };

    const fetchData = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/lead-status/all?projectId=${currentProjectId}`,
          {
            headers: {
              Authorization: `Bearer ${storedUser.accessToken}`,
            },
          },
        );
        setStatuses(res.data || []);
      } catch (err) {
        console.log(err);
      }
    };

    fetchData();
    fetchProjects();
  }, [open, currentProjectId, orgId]);

  const handleTransfer = async () => {
    if (!project || !status) {
      showError("Please select project and status");
      return;
    }

    try {
      setLoading(true);

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/lead/transfer?leadId=${lead.leadId}`,
        {
          orgId: orgId,
          projectId: project.projectId,
          status: status,
        },
        {
          headers: {
            Authorization: `Bearer ${storedUser.accessToken}`,
          },
        },
      );

      onSuccess?.();
      onClose();
    } catch (err) {
      console.log(err);
      showError("Failed to transfer lead");
    } finally {
      setLoading(false);
    }
  };

  const sortedStatus = [...(statuses || [])].sort((a, b) => {
    const groupA = a.metaLeadStatus?.status || "";
    const groupB = b.metaLeadStatus?.status || "";
    if (groupA !== groupB) {
      return groupA.localeCompare(groupB);
    }
    return a.status.localeCompare(b.status);
  });

  return (
    <>
    <Modal
      open={open}
      onClose={onClose}
      slotProps={{ backdrop: { sx: { background: "rgba(15,27,42,0.46)" } } }}
    >
      <Box
        sx={{
          width: 450,
          maxWidth: "95%",
          bgcolor: "#fff",
          p: 3,
          borderRadius: "18px",
          boxShadow: "0 32px 64px -20px rgba(15,27,42,0.5)",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          outline: "none",
          fontFamily: FONT,
          color: INK,
        }}
      >
        <Typography
          variant="h6"
          mb={1.5}
          sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 800, letterSpacing: "-0.02em", color: INK }}
        >
          Transfer Lead
        </Typography>

        <Typography
          mb={2.25}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 1.5,
            py: 0.5,
            borderRadius: "999px",
            background: "#EAF2FB",
            border: "1px solid #C9DEF3",
            fontFamily: FONT,
            fontSize: 13.5,
            fontWeight: 700,
            color: "#1B4F82",
          }}
        >
          {lead?.name}
        </Typography>

        <label style={labelStyle}>Move to project</label>
        <Autocomplete
          options={projects}
          getOptionLabel={(o) => o.projectName || ""}
          value={project}
          onChange={(e, value) => setProject(value)}
          renderInput={(params) => (
            <TextField {...params} placeholder="Select Project" />
          )}
          slotProps={{ paper: { sx: paperSx } }}
          sx={{ mb: 2, ...acSx }}
        />
        <label style={labelStyle}>Status in the new project</label>
        <Autocomplete
          name="status"
          options={sortedStatus || []}
          getOptionLabel={(option) => option.status || ""}
          groupBy={(option) => option.metaLeadStatus.status}
          value={statuses?.find((p) => p.status === status) || null}
          onChange={(e, value) => {
            setStatus(value?.status || "");
          }}
          isOptionEqualToValue={(option, value) =>
            option.status === value.status
          }
          renderInput={(params) => (
            <TextField {...params} placeholder="Select Status" />
          )}
          slotProps={{ paper: { sx: paperSx } }}
          sx={acSx}
        />

        <Box sx={{ display: "flex", gap: 1, mt: 3 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={onClose}
            sx={{
              height: 42,
              borderRadius: "10px",
              borderColor: FIELD_EDGE,
              color: INK,
              fontFamily: FONT,
              fontWeight: 600,
              textTransform: "none",
              "&:hover": { background: "#F4F7FA", borderColor: "#B4C6DA" },
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            sx={{
              height: 42,
              borderRadius: "10px",
              background: PRIMARY,
              fontFamily: FONT,
              fontWeight: 600,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { background: "#084767", boxShadow: "none" },
              "&.Mui-disabled": { background: PRIMARY, color: "#FFFFFF", opacity: 0.6 },
            }}
            disabled={loading}
            onClick={handleTransfer}
          >
            {loading ? "Transferring..." : "Transfer Lead"}
          </Button>
        </Box>
      </Box>
    </Modal>

    <ErrorModal
      open={msgOpen}
      handleClose={() => setMsgOpen(false)}
      msg={msgText}
      type="error"
    />
    </>
  );
};

export default LeadTransferModal;